import React from "react"
import { css, cx } from "@emotion/css"
import { IconButton } from "@material-ui/core"
import {
  addAgencyClientTeamLead,
  deleteAgencyClientTeamLead,
} from "api/clients"
import { queryCache, useMutation } from "react-query"
import { ApiError, ErrorsMap } from "api/http/utils"
import { useParams } from "react-router-dom"
import { ReactComponent as DeleteIcon } from "assets/delete-grey.svg"
import AutoCompleteInput from "components/inputs/AutoComplete"
import { AgencyTeam } from "../../TeamMembers/types"
import { useI18n } from "providers/I18n"
import { debounce, get } from "lodash/fp"
import { useAuthenticated } from "providers/A12n"
import Button from "ui/buttons/Button"
import { TextInput } from "components/inputs/TextInput"
import { TeamLead } from "models/clientAgencyConf/profile"
import { WithAuthority } from "components/WithAuthority"

type Props = {
  role: "PLANNER" | "BUYER"
  agencyId?: string
  teamLeads?: TeamLead[]
  members?: AgencyTeam[]
}

export const AgencyClientTeamLeads = (props: Props) => {
  const { role, agencyId, teamLeads, members } = props
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const { hasAuthority } = useAuthenticated()
  const [openAutoComplete, setOpenAutoComplete] = React.useState<boolean>(false)
  const [searchTerm, setSearchTerm] = React.useState<string>()
  const [errors, setErrors] = React.useState<ErrorsMap>()

  const [create] = useMutation(addAgencyClientTeamLead, {
    onSuccess: () => {
      queryCache.invalidateQueries("agency-client-team-leads")
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const [deleteTeamLead] = useMutation(deleteAgencyClientTeamLead, {
    onSuccess: () => {
      queryCache.invalidateQueries("agency-client-team-leads")
    },
    onError: (err: ApiError) => {
      const error = err as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handleInputChange = debounce(300, (value: string) =>
    setSearchTerm(value),
  )

  const handleChange = (value?: AgencyTeam | null) => {
    setOpenAutoComplete(false)
    !!value &&
      create({
        agencyId: agencyId,
        clientId: id,
        teamLeadId: value.id,
      }).catch((e) => {})
  }

  const handleCloseAutocomplete = () => setOpenAutoComplete(false)

  const handleDeleteTeamLead = React.useCallback(
    (teamLeadId: string) => {
      !!agencyId &&
        deleteTeamLead({ clientId: id, teamLeadId, agencyId: agencyId })
    },
    [deleteTeamLead, id, agencyId],
  )

  const onAddTeamLead = React.useCallback(() => {
    setOpenAutoComplete(true)
  }, [])

  const teamLeadsPerRole = (role: "PLANNER" | "BUYER") =>
    teamLeads?.filter((tl) => tl?.role === role)

  const noTeamLead = !teamLeadsPerRole(role)?.length

  const selectedTeamLeads = teamLeadsPerRole(role)?.map((st) => st?.userId)
  const availableTeamLeads = members?.filter(
    (at) => !selectedTeamLeads?.includes(at.id) && at?.role === role,
  )

  const filteredTeamLeads = availableTeamLeads?.filter((tl) =>
    tl.name.toLowerCase().includes(searchTerm?.toLowerCase() ?? ""),
  )

  return (
    <>
      {!!teamLeadsPerRole(role)?.length ? (
        <div className={cx("BodyBody-2Dark-greyRegular", styles.subTitle)}>
          {t("agency.client.team.leader")}
        </div>
      ) : null}
      <div className={styles.inputWrapped}>
        {teamLeadsPerRole(role)?.map((member, idx) => {
          return (
            <React.Fragment key={idx}>
              <TextInput
                value={`${member.firstName} ${member.lastName}`}
                margin="normal"
                size="medium"
                className={cx(styles.input, styles.disabledInput)}
                disabled={true}
              />
              {hasAuthority(["AUTH_UNASSIGN_AGENCY_CLIENT_TEAM"]) ? (
                <IconButton onClick={() => handleDeleteTeamLead(member.id)}>
                  <DeleteIcon color="inherit" className={styles.deleteIcon} />
                </IconButton>
              ) : (
                <div />
              )}
            </React.Fragment>
          )
        })}
        {openAutoComplete ? (
          <>
            <AutoCompleteInput<AgencyTeam | undefined>
              id="internalApproverId"
              name="internalApproverId"
              label={t("agency.client.team.lead")}
              size="medium"
              getOptionLabel={(member) =>
                `${member?.firstname} ${member?.lastname}`
              }
              options={filteredTeamLeads || []}
              getOptionSelected={(opt?: AgencyTeam, val?: AgencyTeam) =>
                opt?.id === val?.id
              }
              renderOption={(member) => {
                return member ? (
                  <span>
                    {member.firstname} {member.lastname}
                  </span>
                ) : (
                  ""
                )
              }}
              onChange={handleChange}
              onInputChange={handleInputChange}
              blurOnChange={true}
              autoComplete="off"
              error={!!get(`internalApproverId`, errors)}
              helperText={t(get(`internalApproverId`, errors) || "")}
              disabled={!hasAuthority(["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"])}
              className={styles.input}
            />
            <IconButton onClick={handleCloseAutocomplete}>
              <DeleteIcon color="inherit" className={styles.deleteIcon} />
            </IconButton>
          </>
        ) : null}
      </div>
      {noTeamLead && !openAutoComplete ? (
        <div className={styles.centerAligned}>
          {noTeamLead && !openAutoComplete ? (
            <span className="HeadlineH6Dark-greyLight">
              {t("team.lead.empty.subtitlle")}
            </span>
          ) : null}
          <div>
            <WithAuthority allowed={["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"]}>
              <Button
                className={cx(styles.textButton, styles.bold)}
                variant="default"
                color="primary"
                onClick={onAddTeamLead}
              >
                {t("agency.client.teams.add.team.lead")}
              </Button>
            </WithAuthority>
          </div>
        </div>
      ) : (
        <WithAuthority allowed={["AUTH_ASSIGN_AGENCY_CLIENT_TEAM"]}>
          <Button
            className={styles.textButton}
            variant="default"
            color="primary"
            onClick={onAddTeamLead}
            disabled={openAutoComplete || !availableTeamLeads?.length}
          >
            {t("agency.client.teams.add.team.lead")}
          </Button>
        </WithAuthority>
      )}
    </>
  )
}

const styles = {
  subTitle: css`
    padding-left: var(--spacing-xl);
  `,
  inputWrapped: css`
    display: grid;
    grid-template-columns: 1fr 0fr;
    grid-column-gap: var(--spacing-xxs);
    align-items: center;
    justify-content: space-around;
    width: 330px;
  `,
  deleteIcon: css`
    width: 24px;
    height: 24px;
    cursor: pointer;
    color: var(--grey);
  `,
  textButton: css`
    --btn-padding: 8px 0;
    --btn-min-width: 0;
    --btn-line-height: 1;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: transparent;
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;
    &:focus {
      box-shadow: none;
    }
    &[aria-disabled="true"] {
      background-color: transparent;
    }
  `,
  input: css`
    margin-top: var(--spacing-xs);
    .MuiOutlinedInput-notchedOutline {
      border-color: var(--primary-main);
    }
  `,
  disabledInput: css`
    .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline {
      border-color: var(--primary-main);
    }
    .MuiInputBase-input.Mui-disabled {
      color: var(--black);
    }
  `,
  centerAligned: css`
    align-self: center;
    margin: auto;
    display: flex;
    flex-direction: column;
    align-items: center;
  `,
  bold: css`
    --btn-font-weight: bold;
  `,
}
